import cx from 'classnames';
import * as _ from 'lodash';
import { useMemo, useRef } from 'react';
import { Transition } from 'react-transition-group';
import type { CSSProperties, FunctionComponent, ReactNode, ReactNodeArray, RefObject } from 'react';

import { useOnClickOutside, useOnEscPress } from '../../../hooks';
import Icon, { icons } from '../Icon';

import styles from './Drawer.module.less';

export type DrawerProps = {
  /**
   * If the Drawer is open or not
   */
  isOpen: boolean;
  /**
   * Function to close the drawer
   */
  onClose: () => void;
  /**
   * Function to handle back button click. When provided, a back button will be shown.
   */
  onBack?: () => void;
  /**
   * How long the slide animation should last in ms (@default 500)
   */
  slideDuration?: number;
  /**
   * A ref for the element which displayed the Drawer. Usually you want this to be excluded from the useClickOutside handler
   */
  clickTarget?: RefObject<HTMLElement>;
  /**
   * Allows you to temporarily bail out of the click outside behaviour,
   * for example if you have a modal open over top of the drawer.
   */
  shouldSkipClickOutside?: boolean;
  /**
   * "Slot" prop which allows consumers to inject additional buttons to the left of the close button.
   */
  buttonsSlot?: ReactNode | ReactNodeArray;
  /**
   * Override for the % of the viewport that the drawer should take up.
   * Note that its min/max width will still be respected.
   */
  widthPercentage?: number;
  /**
   * Drawer contents
   */
  children: ReactNode;
};

/**
 * Drawer is an overlay-style component which will slide out from the right-hand
 * side of the page and may contain any arbitrary content.
 */
const Drawer: FunctionComponent<DrawerProps> = ({
  children,
  isOpen,
  onClose,
  onBack,
  slideDuration = 500,
  clickTarget,
  shouldSkipClickOutside,
  buttonsSlot,
  widthPercentage,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  //  When the modal is closed, clicking "outside" shouldn't call the onClose handler.
  const handleClose = isOpen ? onClose : _.noop;

  //  Ensure that the drawer is closed if clicked outside of it.
  //  Optionally include the clickTarget which triggered the opening of the Drawer, to ensure it doesn't close.
  useOnClickOutside({
    ref,
    handler: handleClose,
    ignoredElements: clickTarget ? [clickTarget] : undefined,
    shouldSkipClickOutside,
  });
  //  Ensure the drawer is closed when the user hits the Esc key.
  useOnEscPress(handleClose);

  const style: CSSProperties = useMemo(() => {
    if (!widthPercentage) {
      return {
        transitionDuration: `${slideDuration}ms`,
      };
    }
    return {
      transitionDuration: `${slideDuration}ms`,
      //  Override the width set in the stylesheet
      width: `${widthPercentage}%`,
    };
  }, [slideDuration, widthPercentage]);

  return (
    <Transition nodeRef={ref} in={isOpen} timeout={slideDuration}>
      {state => (
        <div
          ref={ref}
          // Id is for resetting the scroll position in licensing issue drawer.
          id="drawer"
          className={cx(styles.drawer, styles[state])}
          style={style}
          aria-hidden={!isOpen}
        >
          <div className={styles.buttonContainer}>
            {onBack && (
              <button type="button" className={styles.backButton} onClick={onBack} aria-label="Back" data-testid="drawer-back-button">
                <Icon icon={icons.angleLeft} />
                <span>Back</span>
              </button>
            )}
            <div className={styles.buttonGroup}>
              {buttonsSlot}
              <button type="button" className={styles.closeButton} onClick={onClose}>
                <Icon icon={icons.times} />
                <span className="sr-only">Close</span>
              </button>
            </div>
          </div>
          {children}
        </div>
      )}
    </Transition>
  );
};

export default Drawer;
